import { useMemo } from 'react';
import { ChoiceDrill } from './ChoiceDrill';
import { makeFigureRound, type Fig } from './figure';
import type { DrillProps } from './meta';

const C = 32, CY = 29, R = 16;
type P = [number, number];

function polygon(sides: number, rot: number): P[] {
  const a0 = -Math.PI / 2 + (rot * Math.PI) / 4;
  return Array.from({ length: sides }, (_, i) => [C + R * Math.cos(a0 + (2 * Math.PI * i) / sides), CY + R * Math.sin(a0 + (2 * Math.PI * i) / sides)] as P);
}

/** 왼쪽 절반(x ≤ 가운데)만 남긴 다각형 — 반 칠하기는 도형이 돌아도 늘 왼쪽 절반 */
function leftHalf(pts: P[]): P[] {
  const out: P[] = [];
  for (let i = 0; i < pts.length; i++) {
    const [x1, y1] = pts[i], [x2, y2] = pts[(i + 1) % pts.length];
    const in1 = x1 <= C, in2 = x2 <= C;
    if (in1) out.push([x1, y1]);
    if (in1 !== in2) out.push([C, y1 + ((C - x1) * (y2 - y1)) / (x2 - x1)]);
  }
  return out;
}

const path = (pts: P[]) => pts.map((p) => p.map((v) => v.toFixed(1)).join(',')).join(' ');
const DOTS: P[] = [[7, 7], [57, 7], [57, 57], [7, 57]];

export function FigSvg({ f, label }: { f: Fig; label?: string }) {
  const pts = polygon(f.sides, f.rot);
  const a = -Math.PI / 2 + (f.rot * Math.PI) / 4;
  return (
    <svg className="fig" viewBox="0 0 64 64" role="img" aria-label={label ?? `도형 변${f.sides} 방향${f.rot} 칠${f.fill} 점${f.dot} 막대${f.n}`}>
      {f.fill === 2 && <polygon points={path(pts)} className="fig-fill" />}
      {f.fill === 1 && <polygon points={path(leftHalf(pts))} className="fig-fill" />}
      <polygon points={path(pts)} className="fig-line" />
      <line x1={C} y1={CY} x2={C + (R - 3) * Math.cos(a)} y2={CY + (R - 3) * Math.sin(a)} className="fig-hand" />
      <circle cx={C} cy={CY} r="2" className="fig-pin" />
      <circle cx={DOTS[f.dot][0]} cy={DOTS[f.dot][1]} r="3.6" className="fig-dot" />
      {Array.from({ length: f.n }, (_, i) => <rect key={i} x={C - (f.n * 5) / 2 + i * 5 + 1} y="52" width="3" height="7" rx="1" className="fig-mark" />)}
    </svg>
  );
}

export function FigureDrill({ level, seed, onDone }: DrillProps) {
  const questions = useMemo(() => makeFigureRound(level, seed), [level, seed]);
  return (
    <ChoiceDrill
      questions={questions} seed={seed} onDone={onDone} className="figure-drill"
      info={(q) => ({ mode: q.mode, rule: q.rule })}
      choicesClass="figs"
      render={(q, revealed) => (
        <div className="figure-q">
          <p className="lang-ask small-ask">{q.mode === 'seq' ? '규칙에 따라 다음에 올 도형은?' : '빈칸에 들어갈 도형은?'}</p>
          <div className={q.mode === 'seq' ? 'fig-row' : 'fig-grid'}>
            {q.cells.map((c, i) => (c ? <div key={i} className="fig-cell"><FigSvg f={c} /></div> : <div key={i} className="fig-cell ask">{revealed ? <FigSvg f={q.options[q.answer]} /> : '?'}</div>))}
            {q.mode === 'seq' && <div className="fig-cell ask">{revealed ? <FigSvg f={q.options[q.answer]} /> : '?'}</div>}
          </div>
        </div>
      )}
      renderChoice={(q, i) => <FigSvg f={q.options[i]} />}
    />
  );
}
